/**
 * KBStepService
 * Ordered steps inside a KBArticle — supports drag-and-drop reorder.
 * Route: api/kb-steps
 */
import { BaseService } from '../base/BaseService';
/** Default empty audience used when the API returns nothing parseable. */
const EMPTY_AUDIENCE = { roleNames: [], ministryIds: [], departmentIds: [] };
export class KBStepService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? KBStepService.DEFAULT_SERVICE_PATH,
            basePath: KBStepService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    getAllRaw() {
        return this.http.get(this.buildUrl(''));
    }
    async getById(id) {
        const response = await this.getByIdRaw(id);
        return response.data ?? null;
    }
    getByIdRaw(id) {
        return this.http.get(this.buildUrl(`${id}`));
    }
    async getByArticle(kbArticleId) {
        const response = await this.getByArticleRaw(kbArticleId);
        return response.data ?? [];
    }
    getByArticleRaw(kbArticleId) {
        return this.http.get(this.buildUrl(`by-article/${kbArticleId}`));
    }
    async create(item) {
        const response = await this.createRaw(item);
        return response.data ?? null;
    }
    createRaw(item) {
        return this.http.post(this.buildUrl(''), item);
    }
    async update(id, item) {
        const response = await this.updateRaw(id, item);
        return response.data ?? null;
    }
    updateRaw(id, item) {
        return this.http.put(this.buildUrl(`${id}`), item);
    }
    /** Reassign OrderNo to match the passed list. Fires KBStepChangedEvent('Reordered'). */
    async reorder(request) {
        const response = await this.http.post(this.buildUrl('reorder'), request);
        return response.success;
    }
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    deleteRaw(id) {
        return this.http.delete(this.buildUrl(`${id}`));
    }
    // ============================================================
    // Visibility — audience management (Restricted steps only)
    // ============================================================
    /**
     * Read the role/ministry/department audience grants for a Restricted
     * KB step. Empty lists == visible to all signed-in users.
     */
    async getAudience(kbStepId) {
        const response = await this.getAudienceRaw(kbStepId);
        return response.data ?? { ...EMPTY_AUDIENCE };
    }
    getAudienceRaw(kbStepId) {
        return this.http.get(this.buildUrl(`${kbStepId}/audience`));
    }
    /** Wholesale replace a KB step's audience grants. */
    async setAudience(kbStepId, audience) {
        const response = await this.setAudienceRaw(kbStepId, audience);
        return response.data ?? audience;
    }
    setAudienceRaw(kbStepId, audience) {
        return this.http.put(this.buildUrl(`${kbStepId}/audience`), audience);
    }
    // ============================================================
    // Streaming media (HLS, transcripts, audio): admin
    // ============================================================
    /** Media processing status for a step (status, error, transcript preview). */
    async getMedia(id) {
        const response = await this.getMediaRaw(id);
        return response.data ?? null;
    }
    getMediaRaw(id) {
        return this.http.get(this.buildUrl(`${id}/media`));
    }
    /** Queue (or with `force`, redo) HLS / transcript / audio processing for a step. */
    async processMedia(id, force = false) {
        const response = await this.processMediaRaw(id, force);
        return response.data ?? null;
    }
    processMediaRaw(id, force = false) {
        const body = { force };
        return this.http.post(this.buildUrl(`${id}/media/process`), body);
    }
}
KBStepService.DEFAULT_SERVICE_PATH = 'resources';
KBStepService.BASE_PATH = 'api/kb-steps';
//# sourceMappingURL=KBStepService.js.map